import { observer } from "mobx-react-lite";
import { useLocalization } from "@progress/kendo-react-intl";
import { useEffect, useRef, useState } from "react";
import { useStores } from "src/stores";
import { useServices } from "src/services";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { Button, ToolbarSpacer } from "@progress/kendo-react-buttons";
import { useHistory } from "react-router";
import { Dealer } from "src/utils/classes";
import { DataResult, process, State } from "@progress/kendo-data-query";
import {
  ColumnMenu,
  ColumnMenuCheckboxFilter,
} from "src/components/GridColumnMenus";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import moment from "moment";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";

interface ExportColumnDef {
  key: string;
  field: string;
  title: string;
}

const DealersScreen = () => {
  // Add this useEffect to inject the CSS for modal styling
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .k-dialog .k-window-titlebar {
        background-color: #007bff !important;
        border-color: #007bff !important;
      }
      .k-dialog .k-window-title {
        color: white !important;
      }
      .k-dialog .k-window-titlebar .k-window-action {
        color: white !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const localizationService = useLocalization();

  const { ui } = useStores();
  const { d4ur } = useServices();
  let history = useHistory();

  const [dataState, setDataState] = useState<State>({
    filter: {
      logic: "and",
      filters: [
        {
          logic: "or",
          filters: [
            { field: "activeGridCell", operator: "eq", value: "Active" },
          ],
        },
      ],
    },
    sort: [
      {
        field: "created_timestamp",
        dir: "desc",
      },
    ],
  });

  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [dataResult, setDataResult] = useState<DataResult>(
    process(dealers, dataState)
  );

  // Year filter state
  const [selectedYear, setSelectedYear] = useState<number>(2026);
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // State for dealer notes functionality
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Add state for dealer notes modal
  const [dealerNotesModalVisible, setDealerNotesModalVisible] = useState<boolean>(false);
  const [selectedDealerNotes, setSelectedDealerNotes] = useState<DealerNote[]>([]);
  const [selectedDealerName, setSelectedDealerName] = useState<string>('');

  // Add state for the add dealer note modal
  const [addDealerNoteModalVisible, setAddDealerNoteModalVisible] = useState<boolean>(false);
  const [selectedDealerForNote, setSelectedDealerForNote] = useState<number | null>(null);
  const [selectedDealerNameForNote, setSelectedDealerNameForNote] = useState<string>('');
  const [newDealerNoteText, setNewDealerNoteText] = useState<string>('');

  // Add state for adding dealer note within the notes modal
  const [showAddDealerNoteInModal, setShowAddDealerNoteInModal] = useState<boolean>(false);
  const [newDealerNoteTextInModal, setNewDealerNoteTextInModal] = useState<string>('');

  // Add state to track the dealer ID for the notes modal
  const [selectedDealerForNoteId, setSelectedDealerForNoteId] = useState<number | null>(null);

  // Add new state for editing dealer notes
  const [editingDealerNoteIndex, setEditingDealerNoteIndex] = useState<number | null>(null);
  const [editedDealerNoteText, setEditedDealerNoteText] = useState<string>('');

  // Add state to store the original dealer note content
  const [originalDealerNoteContent, setOriginalDealerNoteContent] = useState<string>('');

  const processDealers = (dealers: any[]): Dealer[] => {
    if (!Array.isArray(dealers)) {
      return [];
    }
    // remove dupes since we are getting event data in this query and there can be events from mulitple seasons
    return Array.from(new Set(dealers.map((d) => d.dealer_id)))
      .map((dealerId) => dealers.find((d) => d.dealer_id === dealerId))
      .map((d) => new Dealer(d));
  };

  const filterDealersByYear = (dealers: Dealer[], year: number): Dealer[] => {
    return dealers.filter((dealer) => {
      if (!dealer.created_timestamp) return false;
      const dealerYear = moment(dealer.created_timestamp).year();
      return dealerYear === year;
    });
  };

  useEffect(() => {
    // ui.setIsGettingDealers(true);
    d4ur.getDealers().then((ret) => {
      const processedDealers = processDealers(
        (ret as D4URxFetchResult).result || []
      );
      // ui.setIsGettingDealers(false);

      console.log('processedDealers :', processedDealers);
      
      // Extract unique years from dealer data
      const years = Array.from(
        new Set(
          processedDealers
            .filter((d) => d.created_timestamp)
            .map((d) => moment(d.created_timestamp).year())
        )
      ).sort((a, b) => b - a); // Sort descending (newest first)
      
      setAvailableYears(years);
      setDealers(processedDealers);
      
      // Filter by default year (2026)
      const filteredDealers = filterDealersByYear(processedDealers, selectedYear);
      setDataResult(process(filteredDealers, dataState));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dealerNotesModalVisible || addDealerNoteModalVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [dealerNotesModalVisible, addDealerNoteModalVisible]);

  // Filter dealers when year changes
  useEffect(() => {
    const filteredDealers = filterDealersByYear(dealers, selectedYear);
    setDataResult(process(filteredDealers, dataState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const loadDealers = () => {
    return d4ur.getDealers().then((ret) => {
      const processedDealers = processDealers(
        (ret as D4URxFetchResult).result || []
      );

      setDealers(processedDealers);
      
      // Filter by selected year
      const filteredDealers = filterDealersByYear(processedDealers, selectedYear);
      setDataResult(process(filteredDealers, dataState));
      return processedDealers;
    });
  };

  const DealerActionsCell = (props: GridCellProps) => {
    const { dealer_id } = props.dataItem as Dealer;

    return (
      <td>
        <Button
          className="k-button k-primary"
          onClick={() => {
            history.push(`/retailer/${dealer_id}`);
          }}
        >
          Edit
        </Button>
      </td>
    );
  };

  // Dealer Notes Handler Functions
  const handleSaveDealerNote = () => {
    if (!newDealerNoteText.trim()) {
      setErrorMessage('Note text is required.');
      return;
    }

    if (selectedDealerForNote) {
      d4ur.addDealerNoteByDealerId(
        selectedDealerForNote, 
        newDealerNoteText.trim(),
        'admin@example.com', // You may want to get this from user context
        'Admin User' // You may want to get this from user context
      ).then((ret) => {
        if (ret.response.status === 200) {
          setAddDealerNoteModalVisible(false);
          setNewDealerNoteText('');
          setSelectedDealerForNote(null);
          setSelectedDealerNameForNote('');
          // setSuccessMessage('Dealer note added successfully!');
          loadDealers(); // Refresh the data
        } else {
          setErrorMessage(ret.result.message || 'Error adding dealer note.');
        }
      })
      .catch(e => {
        console.log('ADD DEALER NOTE ERROR', e);
        setErrorMessage('Error adding dealer note.');
      });
    }
  };

  const handleSaveDealerNoteInModal = () => {
    if (!newDealerNoteTextInModal.trim()) {
      setErrorMessage('Note text is required.');
      return;
    }

    if (selectedDealerForNoteId) {
      d4ur.addDealerNoteByDealerId(
        selectedDealerForNoteId, 
        newDealerNoteTextInModal.trim(),
        'admin@example.com', // You may want to get this from user context
        'Admin User' // You may want to get this from user context
      ).then((ret) => {
        if (ret.response.status === 200) {
          setNewDealerNoteTextInModal('');
          setShowAddDealerNoteInModal(false);
          
          // Wait for loadDealers to complete, then refresh modal data
          loadDealers().then((updatedDealers) => {
            const updatedDealer = updatedDealers.find(d => d.dealer_id === selectedDealerForNoteId);
            if (updatedDealer) {
              setSelectedDealerNotes(updatedDealer.dealer_notes || []);
            }
          });
          
        } else {
          setErrorMessage(ret.result.message || 'Error adding dealer note.');
        }
      })
      .catch(e => {
        console.log('ADD DEALER NOTE ERROR', e);
        setErrorMessage('Error adding dealer note.');
      });
    }
  };

  const handleSaveEditedDealerNote = (originalTimestamp: string) => {
    if (!editedDealerNoteText.trim()) {
      return;
    }

    if (selectedDealerForNoteId) {
      d4ur.updateDealerNoteByDealerId(
        selectedDealerForNoteId, 
        originalTimestamp,
        originalDealerNoteContent,
        editedDealerNoteText.trim()
      ).then((ret) => {
        if (ret.response.status === 200) {
          setEditingDealerNoteIndex(null);
          setEditedDealerNoteText('');
          setOriginalDealerNoteContent('');
          
          // Update local modal state immediately
          setSelectedDealerNotes(prevNotes => 
            prevNotes.map((note, index) => 
              index === editingDealerNoteIndex 
                ? { ...note, note: editedDealerNoteText.trim() }
                : note
            )
          );
          
          // Also refresh the full data in background
          loadDealers().then((updatedDealers) => {
            const updatedDealer = updatedDealers.find(d => d.dealer_id === selectedDealerForNoteId);
            if (updatedDealer) {
              setSelectedDealerNotes(updatedDealer.dealer_notes || []);
            }
          });
        }
      })
      .catch(e => {
        console.log('UPDATE DEALER NOTE ERROR', e);
      });
    }
  };

  const handleDeleteDealerNote = (originalTimestamp: string, noteContent: string, noteIndex: number) => {
    if (selectedDealerForNoteId) {
      if (window.confirm('Are you sure you want to delete this dealer note?')) {
        d4ur.deleteDealerNoteByDealerId(
          selectedDealerForNoteId,
          originalTimestamp,
          noteContent
        ).then((ret) => {
          if (ret.response.status === 200) {
            
            // Update local modal state immediately by removing the note
            setSelectedDealerNotes(prevNotes => 
              prevNotes.filter((_, index) => index !== noteIndex)
            );
            
            // Also refresh the full data in background
            loadDealers().then((updatedDealers) => {
              const updatedDealer = updatedDealers.find(d => d.dealer_id === selectedDealerForNoteId);
              if (updatedDealer) {
                setSelectedDealerNotes(updatedDealer.dealer_notes || []);
              }
            });
            
          } else {
            setErrorMessage(ret.result.message || 'Error deleting dealer note.');
          }
        })
        .catch(e => {
          console.log('DELETE DEALER NOTE ERROR', e);
          setErrorMessage('Error deleting dealer note.');
        });
      }
    }
  };

  const DealerNotesCell = (props: GridCellProps) => {
    const { dealer_id, dealer_name, dealer_notes } = props.dataItem as Dealer;
    const notesCount = dealer_notes ? dealer_notes.length : 0;
    
    const openDealerNotesModal = () => {
      setSelectedDealerNotes(dealer_notes || []);
      setSelectedDealerName(dealer_name);
      setSelectedDealerForNoteId(dealer_id);
      setShowAddDealerNoteInModal(false);
      setNewDealerNoteTextInModal('');
      setDealerNotesModalVisible(true);
    };

    const openAddDealerNoteModal = () => {
      setSelectedDealerForNote(dealer_id);
      setSelectedDealerNameForNote(dealer_name);
      setNewDealerNoteText('');
      setAddDealerNoteModalVisible(true);
    };
    
    return (
      <td>
        <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "auto",
          }}>
        {/* <label>Dealer Notes</label> */}
        
        {/* Conditional rendering based on notes count */}
        {notesCount > 0 ? (
          <div style={{ display: "flex", alignItems: "center", marginTop: "4px" }}>
            <span style={{ fontSize: "12px" }}>Notes({notesCount})</span>
            <span
              className={'k-icon k-i-edit'}
              style={{ color: '#666', marginLeft: '4px', fontSize: '12px', cursor: 'pointer' }}
              onClick={openDealerNotesModal}
            />
          </div>
        ) : (
          <Button
            className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
            style={{ 
              marginTop: "4px", 
              fontSize: "10px", 
              padding: "2px 8px",
              backgroundColor: "#007bff",
              borderColor: "#007bff"
            }}
            onClick={openAddDealerNoteModal}
          >
            Add Note
          </Button>
        )}
      </div>
    </td>
  );
};

  const FormatExportData = (data: any[]) => {
    return data.map((d: any) => {
      let formattedNotes = '';
      
      if (d.dealer_notes && d.dealer_notes.length) {
        try {
          const notes = JSON.parse(d.dealer_notes);
          if (notes.length) {
            formattedNotes = notes
              .sort((a: any, b: any) => moment(a.created_at).diff(moment(b.created_at)))
              .map((note: any) => {
                const formattedDateTime = moment(note.created_at).format("MMM DD, YYYY hh:mm A");
                return `${formattedDateTime} - ${note.created_by}: ${note.note}`;
              })
              .join('\n');
          }
        } catch (e) {
          console.error('Error parsing dealer notes:', e);
          formattedNotes = '';
        }
      }

      return {
        ...d,
        first_time_dealer: d.first_time_dealer ? 'YES' : null,
        hispanic: d.hispanic ? 'YES' : null,
        dealer_notes: formattedNotes
      };
    });
  };

  const _export = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const exportColumnDefs: ExportColumnDef[] = [
    { key: 'dealer_id', field: 'dealer_id', title: 'Dealer ID' },
    { key: 'dealer_name', field: 'dealer_name', title: 'Dealer Name' },
    { key: 'parts_code', field: 'parts_code', title: 'Parts Code' },
    { key: 'region', field: 'region', title: 'Region' },
    { key: 'address', field: 'address', title: 'Address' },
    { key: 'address2', field: 'address2?', title: 'Address 2' },
    { key: 'city', field: 'city', title: 'City' },
    { key: 'state', field: 'state', title: 'State' },
    { key: 'zip', field: 'zip', title: 'Zip' },
    { key: 'phone', field: 'phone', title: 'Phone' },
    { key: 'primary_contact', field: 'primary_contact', title: 'Contact' },
    { key: 'contact_email', field: 'contact_email', title: 'Contact Email' },
    { key: 'contact_phone', field: 'contact_phone', title: 'Contact Phone' },
    { key: 'secondary_contact', field: 'secondary_contact', title: 'Secondary Contact' },
    { key: 'secondary_contact_email', field: 'secondary_contact_email', title: 'Secondary Contact Email' },
    { key: 'secondary_contact_phone', field: 'secondary_contact_phone', title: 'Secondary Contact Phone' },
    { key: 'tertiary_contact', field: 'tertiary_contact', title: 'Tertiary Contact' },
    { key: 'tertiary_contact_email', field: 'tertiary_contact_email', title: 'Tertiary Contact Email' },
    { key: 'tertiary_contact_phone', field: 'tertiary_contact_phone', title: 'Tertiary Contact Phone' },
    { key: 'created_timestamp', field: 'created_timestamp', title: 'Created' },
    { key: 'ffs_event_id', field: 'ffs_event_id', title: 'FFS Event ID' },
    { key: 'start_date', field: 'start_date', title: 'Event Date' },
    { key: 'training_completed', field: 'training_completed', title: 'Training Completed' },
    { key: 'first_time_dealer', field: 'first_time_dealer', title: '1st Time Dealer' },
    { key: 'hispanic', field: 'hispanic', title: 'Hispanic' },
    { key: 'dealer_notes', field: 'dealer_notes', title: 'Notes' },
  ];

  const [selectedExportColumns, setSelectedExportColumns] = useState<string[]>(
    exportColumnDefs.map((col) => col.key)
  );
  const toggleExportColumn = (key: string) => {
    setSelectedExportColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };
  const selectAllExportColumns = () => setSelectedExportColumns(exportColumnDefs.map((col) => col.key));
  const clearAllExportColumns = () => setSelectedExportColumns([]);
  const selectedExportColumnDefs = exportColumnDefs.filter((col) => selectedExportColumns.includes(col.key));

  const gridContentRef = useRef<HTMLDivElement | null>(null);
  const topScrollbarRef = useRef<HTMLDivElement | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState<number>(3000);

  useEffect(() => {
    if (dealers.length === 0) return;
    const measureWidth = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && gridContent.scrollWidth > 0) setScrollbarWidth(gridContent.scrollWidth);
    };
    const syncTopScrollbar = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && topScrollbarRef.current) topScrollbarRef.current.scrollLeft = gridContent.scrollLeft;
    };
    requestAnimationFrame(() => {
      measureWidth();
      setTimeout(measureWidth, 200);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.addEventListener('scroll', syncTopScrollbar);
    });
    window.addEventListener('resize', measureWidth);
    return () => {
      window.removeEventListener('resize', measureWidth);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.removeEventListener('scroll', syncTopScrollbar);
    };
  }, [dealers.length, dataResult.data.length]);

  return (
    <>
      {/* Add Dealer Note Modal */}
      {addDealerNoteModalVisible && (
        <Dialog
          title={`Add Dealer Note - ${selectedDealerNameForNote}`}
          onClose={() => {
            setAddDealerNoteModalVisible(false);
            setNewDealerNoteText('');
            setSelectedDealerForNote(null);
            setSelectedDealerNameForNote('');
          }}
          width={500}
          height={300}
        >
          <div style={{ padding: "20px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
              Note:
            </label>
            <textarea
              value={newDealerNoteText}
              onChange={(e) => setNewDealerNoteText(e.target.value)}
              placeholder="Enter your dealer note here..."
              style={{
                width: "calc(100% - 20px)",
                height: "120px",
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
                resize: "vertical",
                marginRight: "10px"
              }}
            />
          </div>
          <DialogActionsBar>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => {
                setAddDealerNoteModalVisible(false);
                setNewDealerNoteText('');
                setSelectedDealerForNote(null);
                setSelectedDealerNameForNote('');
              }}
            >
              Cancel
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              style={{
                backgroundColor: "#007bff",
                borderColor: "#007bff",
                color: "white"
              }}
              onClick={handleSaveDealerNote}
              disabled={!newDealerNoteText.trim()}
            >
              Save Note
            </button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Dealer Notes Modal */}
      {dealerNotesModalVisible && (
        <Dialog
          title={`Dealer Notes - ${selectedDealerName}`}
          onClose={() => {
            setDealerNotesModalVisible(false);
            setShowAddDealerNoteInModal(false);
            setNewDealerNoteTextInModal('');
            setSelectedDealerForNoteId(null);
          }}
          width={600}
          height={showAddDealerNoteInModal ? 550 : 400}
        >
          <div style={{ padding: "20px" }}>
            {/* Add Note Section - Moved to top */}
            {showAddDealerNoteInModal ? (
              <div style={{ 
                marginBottom: "20px", 
                padding: "15px", 
                border: "1px solid #007bff", 
                borderRadius: "5px",
                backgroundColor: "#f8f9fa"
              }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                  Add New Dealer Note:
                </label>
                <textarea
                  value={newDealerNoteTextInModal}
                  onChange={(e) => setNewDealerNoteTextInModal(e.target.value)}
                  placeholder="Enter your dealer note here..."
                  style={{
                    width: "100%",
                    height: "80px",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px",
                    resize: "vertical",
                    boxSizing: "border-box"
                  }}
                />
                <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
                  <Button
                    className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                    style={{
                      backgroundColor: "#007bff",
                      borderColor: "#007bff",
                      color: "white"
                    }}
                    onClick={handleSaveDealerNoteInModal}
                    disabled={!newDealerNoteTextInModal.trim()}
                  >
                    Save Note
                  </Button>
                  <Button
                    className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base"
                    onClick={() => {
                      setShowAddDealerNoteInModal(false);
                      setNewDealerNoteTextInModal('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                style={{ 
                  marginBottom: "20px",
                  backgroundColor: "#007bff",
                  borderColor: "#007bff"
                }}
                onClick={() => setShowAddDealerNoteInModal(true)}
              >
                Add Note
              </Button>
            )}

            {selectedDealerNotes.length > 0 ? (
              selectedDealerNotes.map((note, index) => (
                <div 
                  key={index} 
                  style={{ 
                    marginBottom: "20px", 
                    padding: "15px", 
                    border: "1px solid #ddd", 
                    borderRadius: "5px",
                    backgroundColor: "#f9f9f9"
                  }}
                >
                  {editingDealerNoteIndex === index ? (
                    // Edit mode - stack everything vertically
                    <div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Note:</strong>
                        <textarea
                          value={editedDealerNoteText}
                          onChange={(e) => setEditedDealerNoteText(e.target.value)}
                          style={{
                            width: "100%",
                            height: "80px",
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                            marginTop: "5px",
                            boxSizing: "border-box"
                          }}
                        />
                      </div>
                      <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
                        <span style={{ marginRight: "15px" }}>
                          <strong>Created:</strong> {note.created_at}
                        </span>
                        <span>
                          <strong>By:</strong> {note.created_by}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <Button
                          className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                          style={{
                            backgroundColor: "#007bff",
                            borderColor: "#007bff",
                            color: "white"
                          }}
                          onClick={() => handleSaveEditedDealerNote(note.created_at)}
                          disabled={!editedDealerNoteText.trim()}
                        >
                          Save
                        </Button>
                        <Button
                          className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base"
                          onClick={() => {
                            setEditingDealerNoteIndex(null);
                            setEditedDealerNoteText('');
                            setOriginalDealerNoteContent('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View mode - keep original layout
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: "8px" }}>
                          <strong>Note:</strong>
                          <p style={{ margin: "5px 0", lineHeight: "1.4" }}>{note.note}</p>
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          <span style={{ marginRight: "15px" }}>
                            <strong>Created:</strong> {note.created_at}
                          </span>
                          <span>
                            <strong>By:</strong> {note.created_by}
                          </span>
                        </div>
                      </div>
                      <div style={{ marginLeft: "10px" }}>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <Button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                            style={{
                              backgroundColor: "#007bff",
                              borderColor: "#007bff",
                              color: "white"
                            }}
                            onClick={() => {
                              setEditingDealerNoteIndex(index);
                              setEditedDealerNoteText(note.note);
                              setOriginalDealerNoteContent(note.note);
                            }}
                          >
                            Edit
                          </Button>
                          <span
                            className={'k-icon k-i-trash'}
                            style={{ 
                              color: '#dc3545', 
                              fontSize: '16px', 
                              cursor: 'pointer',
                              padding: '8px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            onClick={() => handleDeleteDealerNote(note.created_at, note.note, index)}
                            title="Delete note"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p>No dealer notes available.</p>
            )}
          </div>
          <DialogActionsBar>
            <Button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => {
                setDealerNotesModalVisible(false);
                setShowAddDealerNoteInModal(false);
                setNewDealerNoteTextInModal('');
                setSelectedDealerForNoteId(null);
              }}
            >
              Close
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      <div id="Dashboard" className="dashboard-page main-content">
        <div className="card-container grid">
          <h3 className="card-title">
            {localizationService.toLanguageString("custom.dealers", "dealers")}
          </h3>
          
          {/* Year Filter Dropdown */}
          <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontWeight: 'bold', fontSize: '14px' }}>Filter by Signup Year:</label>
            <DropDownList
              data={availableYears}
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              style={{ width: '150px' }}
            />
            <span style={{ fontSize: '14px', color: '#666' }}>
              ({dataResult.data.length} dealer{dataResult.data.length !== 1 ? 's' : ''})
            </span>
          </div>
          
          {isLoading ? <p style={{ color: 'blue' }}>Processing...</p> : null}
          {errorMessage ? <p style={{ color: 'red' }}>{errorMessage}</p> : null}
          {successMessage ? <p style={{ color: 'green' }}>{successMessage}</p> : null}
          {/* <Button primary={!ui.events.length} onClick={handleEventRequestButton}>
                        { ui.events.length ?
                            localizationService.toLanguageString('custom.requestNewEvent','requestNewEvent') :
                            localizationService.toLanguageString('custom.beginHere','beginHere')
                        }
                    </Button> */}
          <div className="card-component">
            <ExcelExport data={FormatExportData(dataResult.data)} ref={_export} fileName={`dealers_${selectedYear}_export_${moment().format('YYYYMMDD-hhmma')}.xlsx`}>
              {selectedExportColumnDefs.map((col) => (
                <ExcelExportColumn key={col.key} field={col.field} title={col.title} />
              ))}
            </ExcelExport>
            <div style={{ marginBottom: '12px' }}>
              <details>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  Choose Excel columns ({selectedExportColumnDefs.length} selected)
                </summary>
                <div style={{ marginTop: '8px' }}>
                  <button className="k-button k-button-md k-rounded-md" onClick={selectAllExportColumns} type="button" style={{ marginRight: '8px' }}>Select All</button>
                  <button className="k-button k-button-md k-rounded-md" onClick={clearAllExportColumns} type="button">Clear All</button>
                </div>
                <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(220px, 1fr))', gap: '6px 12px' }}>
                  {exportColumnDefs.map((col) => (
                    <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input type="checkbox" checked={selectedExportColumns.includes(col.key)} onChange={() => toggleExportColumn(col.key)} />
                      <span>{col.title}</span>
                    </label>
                  ))}
                </div>
              </details>
            </div>

            <style>{`
              .dealers-grid-wrapper .k-grid-content { overflow-x: scroll; scrollbar-width: none; -ms-overflow-style: none; }
              .dealers-grid-wrapper .k-grid-content::-webkit-scrollbar { display: none; }
              .dealers-top-scrollbar { overflow-x: scroll; overflow-y: hidden; height: 14px; position: sticky; top: 0; z-index: 50; background: #f5f5f5; border-bottom: 1px solid #ddd; }
              .dealers-top-scrollbar::-webkit-scrollbar { height: 8px; }
              .dealers-top-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
              .dealers-top-scrollbar::-webkit-scrollbar-thumb { background: #aaa; border-radius: 4px; }
              .dealers-top-scrollbar::-webkit-scrollbar-thumb:hover { background: #666; }
            `}</style>
            <div style={{ height: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
              <div ref={topScrollbarRef} className="dealers-top-scrollbar" onScroll={(e) => { const gc = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement; if (gc) gc.scrollLeft = e.currentTarget.scrollLeft; }}>
                <div style={{ width: scrollbarWidth, height: 1 }} />
              </div>
              <div ref={gridContentRef} className="dealers-grid-wrapper">
            <Grid
              data={dataResult}
              sortable={{ mode: "multiple" }}
              {...dataState}
              onDataStateChange={(event: GridDataStateChangeEvent) => {
                console.log("event.dataState", event.dataState);
                setDataResult(process(dealers, event.dataState));
                setDataState(event.dataState);
              }}
              resizable={true}
              onColumnResize={() => { const gc = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement; if (gc) setScrollbarWidth(gc.scrollWidth); }}
            >
              <GridToolbar>
                <ToolbarSpacer />
                <button
                  className="k-button k-button-md k-rounded-md k-button-solid"
                  onClick={excelExport}
                  disabled={selectedExportColumnDefs.length === 0}
                >
                  Export {selectedYear} Dealers ({selectedExportColumnDefs.length} columns)
                </button>
              </GridToolbar>
              <GridColumn
                field="dealer_name"
                title={localizationService.toLanguageString(
                  "custom.dealer",
                  "dealer"
                )}
                columnMenu={ColumnMenu}
                width={200}
              />
              <GridColumn
                field="created_timestamp"
                title="Signup Date"
                format="{0:E, MMM dd yyyy}"
                filter="date"
                columnMenu={ColumnMenu}
                width={200}
              />
              <GridColumn
                field="approved_dealers"
                title="Allowed"
                columnMenu={ColumnMenuCheckboxFilter(dealers, {
                  searchBox: () => null,
                })}
                width={150}
              />
              <GridColumn
                field="firstTimeDealerGridCell"
                title="First Time Dealer"
                columnMenu={ColumnMenuCheckboxFilter(dealers, {
                  searchBox: () => null,
                })}
                width={150}
              />
              <GridColumn
                field="hispanicReadable"
                title="Hispanic"
                columnMenu={ColumnMenuCheckboxFilter(dealers, {
                  searchBox: () => null,
                })}
                width={150}
              />
              <GridColumn
                field="activeGridCell"
                title="Status"
                columnMenu={ColumnMenuCheckboxFilter(dealers, {
                  searchBox: () => null,
                })}
                width={100}
              />
              {/* ARCHIVED (D4URX-2752): Dealer Notes are hidden everywhere in the admin; code kept so it can be restored.
                 To restore: delete these two header lines and the closing comment line, and un-escape any star-backslash-slash.
              <GridColumn title="Notes" cell={DealerNotesCell} width={350} />
              */}
              <GridColumn cell={DealerActionsCell} width={300} />
            </Grid>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default observer(DealersScreen);
